import type { ColumnDef, ColumnMeta } from '@tanstack/react-table';
import type { Customer } from '@/services/db/customer/entity';
import { ATTRIBUTES_TO_IGNORE_FOR_TIMESTAMPS, FIXED_COLUMN_ATTRIBUTES } from '../utils/attributes';
import { AttributeCell, CreatedAtCell, EmailCell, UnsubscribedCell } from './cells';

/**
 * `_isPrivateColumn` keeps the fixed columns out of the columns menu; it's a
 * Pluma internal that isn't part of the published `ColumnMeta` augmentation.
 */
type PeopleColumnMeta = ColumnMeta<Customer, unknown> & { _isPrivateColumn?: boolean };

const UNSUBSCRIBED_META: PeopleColumnMeta = { width: 36, _isPrivateColumn: true };
const EMAIL_META: PeopleColumnMeta = { width: '2fr', _isPrivateColumn: true };
const CREATED_AT_META: PeopleColumnMeta = { align: 'end', _isPrivateColumn: true };

/** The id prefix that marks a dynamic attribute column — the contract shared with `columnVisibility`. */
export const ATTRIBUTE_COLUMN_PREFIX = 'custom_';

export function attributeColumnId(attribute: string): string {
  return `${ATTRIBUTE_COLUMN_PREFIX}${attribute}`;
}

export type PeopleColumnsOptions = {
  /** Every known attribute name — one dynamic column each; `columnVisibility` decides which show. */
  allAttributes: readonly string[];
  /** Masquerade/redaction: the created_at column renders `*****`. */
  shouldRedactCreatedAt: boolean;
};

/**
 * The People columns: fixed unsubscribed-indicator and email columns, one
 * column per known attribute (visibility-controlled), and a fixed created_at
 * column. Headers are the raw attribute names, matching what the columns menu
 * and the persisted column set use. Not sortable — the list is always
 * newest-first server-side.
 */
export function createPeopleColumns({
  allAttributes,
  shouldRedactCreatedAt,
}: PeopleColumnsOptions): ColumnDef<Customer>[] {
  const columns: ColumnDef<Customer>[] = [
    {
      id: 'unsubscribed',
      header: '',
      meta: UNSUBSCRIBED_META,
      enableHiding: false,
      cell: (info) => <UnsubscribedCell person={info.row.original} />,
    },
    {
      id: 'email',
      header: 'email',
      meta: EMAIL_META,
      enableHiding: false,
      cell: (info) => <EmailCell person={info.row.original} />,
    },
  ];

  for (const attribute of allAttributes) {
    if (FIXED_COLUMN_ATTRIBUTES.has(attribute)) continue;
    const isTimestampCandidate = !ATTRIBUTES_TO_IGNORE_FOR_TIMESTAMPS.includes(attribute);
    columns.push({
      id: attributeColumnId(attribute),
      header: attribute,
      // Attribute names can themselves contain dots ("dotted.key" is one flat
      // key in the attributes map), so this is a plain index, never a path walk.
      cell: (info) => (
        <AttributeCell value={info.row.original.attributes[attribute]} isTimestampCandidate={isTimestampCandidate} />
      ),
    });
  }

  columns.push({
    id: 'createdAt',
    header: 'created_at',
    meta: CREATED_AT_META,
    enableHiding: false,
    cell: (info) => <CreatedAtCell person={info.row.original} shouldRedact={shouldRedactCreatedAt} />,
  });

  return columns;
}
